import React, { useEffect, useState, useMemo } from 'react';
import T from 'prop-types';
// components
import CenterBox from 'components/CenterBox';
import StickyHeader from 'components/StickyHeader/StickyHeader';
import StickyFooter from 'components/StickyFooter/StickyFooter';
import ReactSelect from 'components/Select/ReactSelect';
import SingleDatePicker from 'components/DatePicker/SingleDatePicker/SingleDatePicker';
import Button from 'components/Button';
import CommentBox from 'components/CommentBox';
import RangeInput from 'components/RangeInput/RangeInput';
import MessageBox from 'components/MessageBox';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import Input from 'components/Input';
import { FormattedMessage } from 'react-intl';
import Link from 'react-router/lib/Link';
import cn from 'classnames';
import moment from 'moment';
import { animateScroll } from 'react-scroll';
import { convertLbsToTons, formatBackEndWeightToSystem, convertKgToLbs } from 'utils/weightHelper';
import { formatNumber } from 'utils';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import './FeedOrderForm.scss';
import { withRouter } from 'react-router';
import { getFeedDefaultDeliveryDate } from 'utils/feedDeliveryDate';
import mixpanel from 'mixpanel-browser';
// regional date handling
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { getInputPlaceholder } from 'utils/regionalDateHelper';
import { connect } from 'react-redux';

const defaultBin = { id: null, bin_id: null, quantity: 0 };

const FeedOrderForm = ({
  initialValues,
  onSubmit,
  binsOptions,
  backLink,
  title,
  submitLabel,
  farmName,
  measurementSystem = 'imperial',
  openModal,
  countryCode,
  feedDefaultDeliveryDate,
  feedDefaultDeliveryCutoffTime,
  feedDeliveryDayDelays,
}) => {
  const [notes, setNotes] = useState(initialValues.notes || '');
  const [feedOrderBins, setFeedOrderBins] = useState(initialValues.feed_order_bins_attributes?.length
    ? initialValues.feed_order_bins_attributes.map((b) => ({ ...b }))
    : [defaultBin]);
  const [destroyedBins, setDestroyedBins] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState(initialValues.delivery_date || null);
  const [isLoading, setIsLoading] = useState(false);
  const [dateValidationError, setDateValidationError] = useState(null);
  const [editingQuantityKey, setEditingQuantityKey] = useState(null);
  const [quantityInputValueByKey, setQuantityInputValueByKey] = useState({});

  const regionalPlaceholder = getInputPlaceholder(countryCode);

  useEffect(() => {
    const hasOnlyDefaultBin =
      initialValues.feed_order_bins_attributes?.length === 1
      && initialValues.feed_order_bins_attributes[0].bin_id === null
      && initialValues.feed_order_bins_attributes[0].quantity === 0;

    if ((hasOnlyDefaultBin || !initialValues.feed_order_bins_attributes?.length) && binsOptions.length) {
      const allBins = binsOptions.map((b) => ({
        bin_id: b.id,
        quantity: 0,
      }));
      setFeedOrderBins(allBins);
    }
  }, [binsOptions, initialValues.feed_order_bins_attributes]);

  useEffect(() => {
    if (!initialValues.delivery_date) {
      setDeliveryDate(getFeedDefaultDeliveryDate(moment(), {
        enabled: feedDefaultDeliveryDate,
        cutoffTime: feedDefaultDeliveryCutoffTime,
        dayDelays: feedDeliveryDayDelays,
      }));
    }
  }, [initialValues.delivery_date]);

  const isToday = (date) => date && date.isSame(moment(), 'day');
  const isWeekend = (date) => date && (date.day() === 6 || date.day() === 0);

  const showSameDayWarning = isToday(deliveryDate);
  const showWeekendWarning = isWeekend(deliveryDate);

  const onNotesOpen = () => {
    animateScroll.scrollToBottom({ containerId: 'bodybag' });
    // Mixpanel: трекінг додавання нотатки
    mixpanel.track('Feature Used', {
      feature_name: 'FeedOrder',
      action: 'Add Note',
      timestamp: new Date().toISOString()
    });
  };

  const totalOrderQuantity = feedOrderBins.reduce((acc, bin) => acc + (bin.quantity || 0), 0);

  const isContinueDisabled = () => {
    if (!deliveryDate) return true;

    const validBinExists = feedOrderBins.some((bin) => bin.bin_id && bin.quantity > 0);
    return !validBinExists;
  };

  const removeBin = (index) => {
    setFeedOrderBins((prevBins) => {
      const binToRemove = prevBins[index];
      const newBins = [...prevBins.slice(0, index), ...prevBins.slice(index + 1)];
      if (binToRemove.id) {
        setDestroyedBins((prevDestroyedBins) => [...prevDestroyedBins, { ...binToRemove, _destroy: true }]);
      }
      return newBins;
    });
  };

  const onBinSelect = (index) => ({ id }) => {
    const selectedBin = binsOptions.find((bOption) => bOption.id === id);
    const newBinCapacity = selectedBin?.capacity || 30000;

    setFeedOrderBins((prev) => {
      const currentQuantity = prev[index].quantity;
      const clampedQuantity =
        currentQuantity > newBinCapacity ? newBinCapacity : currentQuantity;

      return [
        ...prev.slice(0, index),
        {
          ...prev[index],
          bin_id: id,
          quantity: clampedQuantity,
        },
        ...prev.slice(index + 1),
      ];
    });
  };

  const onBinAdd = () => setFeedOrderBins((prevBins) => [...prevBins, defaultBin]);

  const setBinData = (index) => (key, value) => {
    setFeedOrderBins((prevState) => [
      ...prevState.slice(0, index),
      { ...prevState[index], [key]: value },
      ...prevState.slice(index + 1)
    ]);
  };

  const hideTrash = (feedOrderBins.length === 1 && !feedOrderBins[0].id);

  const submitData = async () => {
    if (isContinueDisabled()) return;

    try {
      setIsLoading(true);
      const validBins = feedOrderBins.filter(
        (bin) => bin.bin_id && bin.quantity > 0
      );
      const allBins = [...validBins, ...destroyedBins];
      const resource = {
        delivery_date: deliveryDate ? deliveryDate.format('YYYY-MM-DD') : null,
        notes,
        feed_order_bins_attributes: allBins.map((bin) => {
          return {
            id: bin.id,
            bin_id: bin.bin_id,
            quantity: bin.quantity,
            _destroy: bin._destroy
          };
        }),
      };
      await onSubmit(resource);
    } catch (error) {
      toastResponseErrors(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitClick = async () => {
    // Mixpanel: трекінг сабміту замовлення
    mixpanel.track('Feature Used', {
      feature_name: 'FeedOrder',
      action: 'Submit Feed Order',
      timestamp: new Date().toISOString()
    });
    if (showSameDayWarning) {
      openModal(
        <ConfirmationModal
          title={<FormattedMessage id="general.feedOrders" />}
          warningMessage={<FormattedMessage id="component.modal.feedOrderDeliveryDate.sameDayWarning" />}
          actionBtnLabel={<FormattedMessage id="general.button.yes" />}
          actionBtnProps={{ primary: true }}
          handleConfirm={async () => {
            await submitData();
          }}
        />
      );
    } else {
      await submitData();
    }
  };

  const TotalQuantityLine = ({ value, mobile, className }) => {
    const displayValue = formatBackEndWeightToSystem(value, measurementSystem);
    const displayUnit = measurementSystem === 'metric' ? 'general.weight.kg' : 'general.weight.lbs';
    const tonsValue = convertLbsToTons(value);

    return (
      <div className={cn(`total-quantity-line ${className}`, { 'mobile': mobile })}>
        <span className="left-value">
          <span className="icon fa fa-ep-piggy" />
          <FormattedMessage id="general.totalOrder" />
        </span>
        <div className="quantity-container">
          <div className="quantity-lbs">
            <strong>
              {formatNumber(displayValue)}&nbsp;
              <FormattedMessage id={displayUnit} />
            </strong>
          </div>
          <div className="quantity-tons">
            <small>
              (<FormattedMessage
                id="general.tonsValue"
                values={{ value: formatNumber(tonsValue.toFixed(1)) }}
              />)
            </small>
          </div>
        </div>
      </div>
    );
  };

  TotalQuantityLine.propTypes = {
    value: T.number.isRequired,
    mobile: T.bool,
    className: T.string,
  };

  const binsOptionsWithCapacity = useMemo(() => {
    return binsOptions.map((b) => {
      if (!b.capacity) {
        return {
          ...b,
          name: b.source_type === 'manual' ? `${b.name} (manual)` : b.name,
        };
      }

      const displayCapacity = Math.round(formatBackEndWeightToSystem(b.capacity, measurementSystem));
      const unit = measurementSystem === 'metric' ? 'kg' : 'lbs';
      const manualLabel = b.source_type === 'manual' ? ' manual' : '';

      return {
        ...b,
        name: `${b.name} (${formatNumber(displayCapacity)} ${unit})${manualLabel}`,
      };
    });
  }, [binsOptions, measurementSystem]);

  const availableBinsCount = useMemo(() => {
    const selectedBinIds = feedOrderBins.map((bin) => bin.bin_id).filter(Boolean);
    return binsOptions.filter((option) => !selectedBinIds.includes(option.id)).length;
  }, [feedOrderBins, binsOptions]);

  const emptyBinsCount = useMemo(() => {
    return feedOrderBins.filter((bin) => !bin.bin_id).length;
  }, [feedOrderBins]);

  const canAddBin = availableBinsCount > emptyBinsCount;

  return (
    <CenterBox
      className="DailyCheckupOrderFeed"
      isLoading={isLoading}
      renderIf
    >
      <StickyHeader className="hide-for-large">
        <TotalQuantityLine mobile value={totalOrderQuantity} />
      </StickyHeader>

      <section className="center-box-header show-for-large">
        <div>
          {farmName && <div className="subtitle">{farmName}</div>}
          <h1 className="title">
            {title || <FormattedMessage id="general.feedOrder" />}
          </h1>
        </div>
        <Link to={backLink}>
          <i className="fa fa-times close-center-box" />
        </Link>
      </section>

      <TotalQuantityLine mobile={false} value={totalOrderQuantity} className="show-for-large" />

      <section className="center-box-body">
        <div className="migrations-form">
          {feedOrderBins.map((bin, index) => {
            const selectedBin = binsOptions.find((bOption) => bOption.id === bin.bin_id);
            const maxCapacity = selectedBin?.capacity || 30000;
            const isMetric = measurementSystem === 'metric';
            const quantityKey = bin.id ? `id:${bin.id}` : `idx:${index}`;
            const displayQuantityValue = Math.round(formatBackEndWeightToSystem(bin.quantity || 0, measurementSystem));
            const displayMaxCapacity = Math.round(formatBackEndWeightToSystem(maxCapacity, measurementSystem));
            const isEditingThisQuantity = editingQuantityKey === quantityKey;
            const quantityInputValue = isEditingThisQuantity
              ? (quantityInputValueByKey[quantityKey] ?? String(displayQuantityValue || 0))
              : String(displayQuantityValue || 0);

            return (
              <div className="block" key={`bin-${index}`}>
                <div className="input-row">
                  <div className="index-cell">
                    <div className="row-index">{index + 1}</div>
                  </div>
                  <div className="input-group">
                    <ReactSelect
                      filterOptions={(options) => {
                        const selectedBinIds = feedOrderBins
                          .filter((_, i) => i !== index)
                          .map((b) => b.bin_id);
                        return options.filter((option) => !selectedBinIds.includes(option.id));
                      }}
                      className="select-wrapper"
                      placeholder={<FormattedMessage id="general.selectBin" />}
                      options={binsOptionsWithCapacity}
                      valueKey="id"
                      labelKey="name"
                      value={bin.bin_id}
                      onChange={onBinSelect(index)}
                      noInputMargin
                      searchable={false}
                    />
                    <div className="input-group__item range-input">
                      <span className="input-label-group">
                        <span className="input-label"><FormattedMessage id="general.orderQuantity" /></span>
                        <span className="bin-quantity-label">
                          <div className="bin-quantity-lbs">
                            <Input
                              className="bin-quantity-input"
                              type="number"
                              min="0"
                              max={displayMaxCapacity}
                              step={isMetric ? 100 : 1}
                              pattern="[0-9]*"
                              hasNoMargin
                              alignRight
                              bolded
                              placeholder={0}
                              value={quantityInputValue}
                              onFocus={(e) => {
                                setEditingQuantityKey(quantityKey);
                                setQuantityInputValueByKey((prev) => ({
                                  ...prev,
                                  [quantityKey]: String(displayQuantityValue || 0),
                                }));
                                e.target.select();
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setEditingQuantityKey(quantityKey);
                                setQuantityInputValueByKey((prev) => ({ ...prev, [quantityKey]: raw }));

                                if (raw === '') return;

                                const numeric = Number(raw);
                                if (!Number.isFinite(numeric)) return;

                                if (isMetric) {
                                  const clampedKg = Math.max(0, Math.min(displayMaxCapacity, numeric));
                                  const lbsValue = convertKgToLbs(clampedKg);
                                  const clampedLbs = Math.max(0, Math.min(maxCapacity, lbsValue));
                                  setQuantityInputValueByKey((prev) => ({
                                    ...prev,
                                    [quantityKey]: String(clampedKg),
                                  }));
                                  setBinData(index)('quantity', clampedLbs);
                                } else {
                                  const clampedDisplay = Math.max(0, Math.min(displayMaxCapacity, numeric));
                                  const clampedLbs = Math.max(0, Math.min(maxCapacity, clampedDisplay));
                                  setQuantityInputValueByKey((prev) => ({
                                    ...prev,
                                    [quantityKey]: String(clampedDisplay),
                                  }));
                                  setBinData(index)('quantity', clampedLbs);
                                }
                              }}
                              onBlur={() => {
                                const raw = quantityInputValueByKey[quantityKey];

                                if (raw === '') {
                                  setBinData(index)('quantity', 0);
                                } else {
                                  const numeric = Number(raw);
                                  if (Number.isFinite(numeric)) {
                                    if (isMetric) {
                                      const clampedKg = Math.max(0, Math.min(displayMaxCapacity, numeric));
                                      const lbsValue = convertKgToLbs(clampedKg);
                                      const clampedLbs = Math.max(0, Math.min(maxCapacity, lbsValue));
                                      setBinData(index)('quantity', clampedLbs);
                                    } else {
                                      const clampedDisplay = Math.max(0, Math.min(displayMaxCapacity, numeric));
                                      const clampedLbs = Math.max(0, Math.min(maxCapacity, clampedDisplay));
                                      setBinData(index)('quantity', clampedLbs);
                                    }
                                  }
                                }

                                setEditingQuantityKey(null);
                                setQuantityInputValueByKey((prev) => {
                                  const next = { ...prev };
                                  delete next[quantityKey];
                                  return next;
                                });
                              }}
                            />
                            <span className="bin-quantity-unit">
                              <FormattedMessage
                                id={measurementSystem === 'metric' ? 'general.weight.kg' : 'general.weight.lbs'}
                              />
                            </span>
                          </div>
                          <div className="bin-quantity-tons">
                            <small>
                              (<FormattedMessage
                                id="general.tonsValue"
                                values={{ value: formatNumber(convertLbsToTons(bin.quantity || 0).toFixed(1)) }}
                              />)
                            </small>
                          </div>
                        </span>
                      </span>
                      <div className="range-container">
                        <RangeInput
                          step={measurementSystem === 'metric' ? 220 : 1000}
                          max={maxCapacity}
                          label={(
                            <FormattedMessage
                              id={measurementSystem === 'metric' ? 'general.weight.kg' : 'general.weight.lbs'}
                            />
                          )}
                          value={bin.quantity || 0}
                          onChange={(value) => setBinData(index)('quantity', value)}
                          measurementSystem={measurementSystem}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={cn('trash-cell', { 'hidden': hideTrash })}>
                    {!hideTrash && (
                      <i className="fa fa-trash-o trash-icon" onClick={() => removeBin(index)} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {canAddBin && (
          <button className="add-bin-button" onClick={onBinAdd}>
            <FormattedMessage id="general.addAnotherBin" />
          </button>
        )}
        <div className="input-group__item" style={{ marginTop: '20px' }}>
          <span className="input-label"><FormattedMessage id="general.deliveryDate" /></span>
          <SingleDatePicker
            inputId="deliveryDateInput"
            key={`deliveryDate-${deliveryDate ? deliveryDate.format('YYYY-MM-DD') : 'null'}`}
            className="date-input"
            date={deliveryDate}
            onDateChange={(date) => {
              setDeliveryDate(date);
              setDateValidationError(null); // Clear validation error when date changes
            }}
            inputLabel={regionalPlaceholder}
            isOutsideRange={(day) => !(day.isSameOrAfter(moment(), 'day'))}
          />
          {dateValidationError && (
            <div className="mt-5 text-red">
              {dateValidationError}
            </div>
          )}

          {showWeekendWarning && (
            <div className="mt-10">
              <MessageBox type="warning" className="message-box">
                <FormattedMessage id="general.feedOrder.weekendWarning" />
              </MessageBox>
            </div>
          )}

          {!showWeekendWarning && showSameDayWarning && (
            <div className="mt-10">
              <MessageBox type="warning" className="message-box">
                <FormattedMessage id="general.feedOrder.sameDayWarning" />
              </MessageBox>
            </div>
          )}
        </div>
        <CommentBox
          onOpen={onNotesOpen}
          onValueChange={(newNotes) => setNotes(newNotes)}
          value={notes}
          isOpen={!!notes}
        />
      </section>

      <section className="center-box-footer show-for-large">
        <div className="buttons text-right">
          <Link className="button margin-right-1" to={backLink}>
            <FormattedMessage id="general.button.cancel" />
          </Link>
          <Button
            primary
            disabled={isContinueDisabled()}
            className="submit wider"
            onClick={handleSubmitClick}
          >
            {submitLabel || <FormattedMessage id="general.button.continue" />}
          </Button>
        </div>
      </section>
      <StickyFooter className="hide-for-large" hidden={isContinueDisabled()}>
        <Button
          stickyFooter
          primary
          disabled={isContinueDisabled()}
          onClick={handleSubmitClick}
        >
          {submitLabel || <FormattedMessage id="general.button.continue" />}
        </Button>
      </StickyFooter>
    </CenterBox>
  );
};

FeedOrderForm.propTypes = {
  initialValues: T.shape({
    delivery_date: T.object,
    comment: T.string,
    mentionsIds: T.arrayOf(T.number),
    feed_order_bins_attributes: T.arrayOf(T.shape({
      id: T.number,
      bin_id: T.number,
      quantity: T.number
    }))
  }),
  onSubmit: T.func.isRequired,
  binsOptions: T.arrayOf(T.shape({
    id: T.number,
    name: T.string
  })),
  backLink: T.oneOfType([T.string, T.object]).isRequired,
  title: T.node,
  submitLabel: T.node,
  farmName: T.string,
  measurementSystem: T.oneOf(['imperial', 'metric']),
  openModal: T.func.isRequired,
  countryCode: T.string,
  feedDefaultDeliveryDate: T.bool,
  feedDefaultDeliveryCutoffTime: T.string,
  feedDeliveryDayDelays: T.arrayOf(T.number),
};

FeedOrderForm.defaultProps = {
  initialValues: {
    delivery_date: null,
    comment: '',
    mentionsIds: [],
    feed_order_bins_attributes: [{ bin_id: null, quantity: 0 }]
  },
  binsOptions: [],
  countryCode: 'US'
};

export default connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
    feedDefaultDeliveryDate: state.auth.user.current_company.feed_default_delivery_date,
    feedDefaultDeliveryCutoffTime: state.auth.user.current_company.feed_default_delivery_cutoff_time,
    feedDeliveryDayDelays: state.auth.user.current_company.feed_delivery_day_delays,
  })
)(withRouter(FeedOrderForm));
